import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hubAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Rocket, Plus, ChevronRight, ChevronDown, ChevronUp, Target, Layers, Sparkles, FolderKanban, AlertTriangle, CheckCircle2, TimerOff, Search, Lightbulb, HelpCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Hint, InfoHint } from '@/components/common/Hint';
import { GeneralWall } from '@/components/hub/GeneralWall';
import { AlertsPanel } from '@/components/hub/AlertsPanel';
import { HubFileSearchDialog } from '@/components/hub/HubFileSearchDialog';
import { ActivityFeed } from '@/components/hub/ActivityFeed';
import { HubIntroTour } from '@/components/hub/HubIntroTour';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const ICON_MAP = {
    rocket: Rocket,
    target: Target,
    layers: Layers,
    sparkles: Sparkles,
    folder: FolderKanban,
};

export default function HubHome() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [access, setAccess] = useState(null);
    const [summary, setSummary] = useState({ active: 0, overdue: 0, closed: 0, mine: 0, stage_blocked: 0 });
    const [spaces, setSpaces] = useState([]);
    const [themes, setThemes] = useState([]);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [tourOpen, setTourOpen] = useState(false);
    const [newSpace, setNewSpace] = useState({ name: '', description: '', color: '#6366F1', icon: 'rocket' });

    const load = async () => {
        try {
            setLoading(true);
            const [aR, sR, spR, thR, mR] = await Promise.all([
                hubAPI.access(),
                hubAPI.summary(),
                hubAPI.listSpaces(),
                hubAPI.listThemes().catch(() => ({ data: [] })),
                hubAPI.members().catch(() => ({ data: [] })),
            ]);
            setAccess(aR.data);
            setSummary(sR.data);
            setSpaces(spR.data);
            setThemes(thR.data);
            setMembers(mR.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur de chargement du Hub");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const isAdmin = access?.is_admin;
    const isAdminFull = access?.is_admin_full;

    // État pour la boîte de dialogue de suppression d'espace.
    // { id, name, projectsCount } ou null si aucune suppression en cours.
    const [spaceToDelete, setSpaceToDelete] = useState(null);
    const [deletingSpace, setDeletingSpace] = useState(false);

    const handleDeleteSpace = async (force = false) => {
        if (!spaceToDelete) return;
        setDeletingSpace(true);
        try {
            const res = await hubAPI.deleteSpace(spaceToDelete.id, force);
            const cascaded = res.data?.cascaded_projects || 0;
            toast.success(
                cascaded > 0
                    ? `Espace supprimé (${cascaded} projet${cascaded > 1 ? 's' : ''} supprimé${cascaded > 1 ? 's' : ''})`
                    : 'Espace supprimé',
            );
            setSpaceToDelete(null);
            load();
        } catch (e) {
            const detail = e.response?.data?.detail || 'Erreur de suppression';
            toast.error(detail);
        } finally {
            setDeletingSpace(false);
        }
    };

    const submitCreateSpace = async () => {
        if (!newSpace.name.trim()) { toast.error('Nom obligatoire'); return; }
        try {
            await hubAPI.createSpace(newSpace);
            toast.success('Espace créé');
            setCreating(false);
            setNewSpace({ name: '', description: '', color: '#6366F1', icon: 'rocket' });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur création');
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="p-6 text-sm text-slate-500" data-testid="hub-loading">Chargement du Hub…</div>
            </DashboardLayout>
        );
    }

    if (!access || access.access === 'none') {
        return (
            <DashboardLayout>
                <div className="max-w-2xl mx-auto p-8 text-center" data-testid="hub-no-access">
                    <Rocket className="w-16 h-16 mx-auto text-slate-300" />
                    <h2 className="mt-4 text-xl font-semibold text-slate-800">Accès Projets non activé</h2>
                    <p className="mt-2 text-sm text-slate-500">
                        Un administrateur doit vous accorder l&apos;accès au Hub Projets. Rendez-vous dans Paramètres → Utilisateurs.
                    </p>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
        <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6" data-testid="hub-home">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
                        <Rocket className="w-8 h-8 text-indigo-500" />
                        Projets
                        <InfoHint side="right" text="Le module Projets permet à ton équipe de piloter tous les chantiers internes (fiches CEE, dispositifs, R&D, marketing…) façon kanban. Chaque projet a une importance, une deadline et un but final — pour ne rien lâcher en cours de route." />
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Pilotez chaque projet jusqu&apos;à son issue — kanban visuel, thèmes paramétrables, aucune affaire abandonnée en cours de route.
                    </p>
                </div>
                {isAdmin && (
                    <Hint text="Un espace regroupe plusieurs projets liés (ex: Marketing, R&D, Legal…). Créez autant d'espaces que de domaines d'activité à piloter." side="left">
                        <Button onClick={() => setCreating(true)} data-testid="btn-create-space" className="gap-2">
                            <Plus className="w-4 h-4" /> Nouvel espace
                        </Button>
                    </Hint>
                )}
                <Hint text="Retrouvez n'importe quel PDF, image, vidéo ou vocal partagé dans les murs de vos projets — même après plusieurs semaines." side="left">
                    <Button onClick={() => setSearchOpen(true)} variant="outline" className="gap-2" data-testid="btn-hub-file-search">
                        <Search className="w-4 h-4" /> Rechercher un fichier
                    </Button>
                </Hint>
                <Hint text="Rejouer la présentation guidée du Hub Projets — 8 étapes pour comprendre tout l'environnement en 2 minutes." side="left">
                    <Button onClick={() => setTourOpen(true)} variant="outline" className="gap-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid="btn-hub-intro-tour">
                        <HelpCircle className="w-4 h-4" /> Revoir la visite
                    </Button>
                </Hint>
            </div>

            {/* KPIs — cliquable pour ouvrir la liste filtrée */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <KpiCard icon={FolderKanban} label="Projets actifs" hint="Nombre total de projets en cours (tous espaces, tous thèmes). N'inclut pas les projets gagnés ou clôturés." value={summary.active} color="text-indigo-600" testId="kpi-active" onClick={() => navigate('/projets/liste?filter=active')} />
                <KpiCard icon={Target} label="Mes projets" hint="Projets où vous êtes explicitement membre. Concentrez-vous ici en priorité — ce sont vos responsabilités actives." value={summary.mine} color="text-blue-600" testId="kpi-mine" onClick={() => navigate('/projets/liste?filter=mine')} />
                <KpiCard icon={TimerOff} label="En retard" hint="Projets dont l'étape actuelle a dépassé sa durée max (paramétrée par étape dans chaque thème). Règle : jours passés sur l'étape > max_days. Signal d'alerte n°1 : à débloquer en priorité." value={summary.stage_blocked} color="text-red-600" testId="kpi-stage-blocked" onClick={() => navigate('/projets/liste?filter=stage_blocked')} />
                <KpiCard icon={AlertTriangle} label="Deadline passée" hint="Info : projets actifs dont la deadline globale est passée. Peut rester longtemps sans être « en retard » au sens du pipeline — c'est juste une date butoir informative." value={summary.overdue} color="text-slate-500" testId="kpi-overdue" onClick={() => navigate('/projets/liste?filter=overdue')} />
                <KpiCard icon={CheckCircle2} label="Clôturés" hint="Projets terminés (gagnés ou clôturés avec commentaire). Consultez-les via le filtre « Clôturés » dans chaque espace." value={summary.closed} color="text-emerald-600" testId="kpi-closed" onClick={() => navigate('/projets/liste?filter=closed')} />
            </div>

            {/* Panneau d'alertes intelligentes — apparaît uniquement s'il y en a */}
            <AlertsPanel />

            {/* Fil d'activité — mouvements récents sur tous les projets (cliquable) */}
            <ActivityFeed />

            {/* Spaces — priorité opérationnelle, remontés au-dessus pour être immédiatement visibles */}
            <div>
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    Espaces de travail
                    <InfoHint text="Un espace = un domaine ou un département. Cliquez sur une carte pour ouvrir son kanban de projets." side="right" />
                </h2>
                {spaces.length === 0 ? (
                    <Card><CardContent className="p-8 text-center text-slate-500 text-sm">Aucun espace. {isAdmin && 'Cliquez sur « Nouvel espace » pour en créer un.'}</CardContent></Card>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {spaces.map((s) => {
                            const Icon = ICON_MAP[s.icon] || Rocket;
                            return (
                                <div key={s.id} className="relative group">
                                    <button
                                        onClick={() => navigate(`/projets/espace/${s.id}`)}
                                        className="w-full text-left rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-lg hover:border-indigo-300 transition-all"
                                        data-testid={`space-card-${s.id}`}
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${s.color}20` }}>
                                                <Icon className="w-6 h-6" style={{ color: s.color }} />
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" />
                                        </div>
                                        <div className="font-semibold text-slate-900">{s.name}</div>
                                        {s.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{s.description}</p>}
                                        <div className="mt-4 flex items-center gap-2">
                                            <Badge variant="secondary" className="text-[11px]">
                                                {s.projects_count} projet{s.projects_count > 1 ? 's' : ''} actif{s.projects_count > 1 ? 's' : ''}
                                            </Badge>
                                            {s.is_default && <Badge className="text-[11px] bg-indigo-100 text-indigo-700 hover:bg-indigo-100">Par défaut</Badge>}
                                        </div>
                                    </button>
                                    {/* Corbeille admin (Hub) — apparaît au hover, en absolu pour ne pas gêner la mise en page */}
                                    {isAdmin && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                setSpaceToDelete({ id: s.id, name: s.name, projectsCount: s.projects_count || 0 });
                                            }}
                                            className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1.5 rounded-lg bg-white/95 hover:bg-red-50 text-slate-400 hover:text-red-600 border border-slate-200 hover:border-red-200 shadow-sm"
                                            title="Supprimer cet espace"
                                            data-testid={`space-delete-btn-${s.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Mur d'idées & propositions — repliable pour ne pas masquer les espaces */}
            <CollapsibleWall
                spaces={spaces}
                themes={themes}
                members={members}
                canCreateProjects={access?.can_create_projects}
                onProjectCreated={load}
            />

            {/* Dialog création espace */}
            <Dialog open={creating} onOpenChange={setCreating}>
                <DialogContent data-testid="create-space-dialog">
                    <DialogHeader>
                        <DialogTitle>Nouvel espace de travail</DialogTitle>
                        <DialogDescription>Un espace regroupe des projets (ex : Marketing, R&D, Legal…).</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Nom</Label>
                            <Input data-testid="new-space-name" value={newSpace.name} onChange={(e) => setNewSpace({ ...newSpace, name: e.target.value })} placeholder="Ex : Marketing" />
                        </div>
                        <div>
                            <Label>Description (optionnel)</Label>
                            <Textarea data-testid="new-space-desc" value={newSpace.description} onChange={(e) => setNewSpace({ ...newSpace, description: e.target.value })} rows={2} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Couleur</Label>
                                <Input type="color" value={newSpace.color} onChange={(e) => setNewSpace({ ...newSpace, color: e.target.value })} className="h-10 p-1" />
                            </div>
                            <div>
                                <Label>Icône</Label>
                                <select
                                    value={newSpace.icon}
                                    onChange={(e) => setNewSpace({ ...newSpace, icon: e.target.value })}
                                    className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
                                >
                                    <option value="rocket">🚀 Fusée</option>
                                    <option value="target">🎯 Cible</option>
                                    <option value="layers">📚 Couches</option>
                                    <option value="sparkles">✨ Étincelles</option>
                                    <option value="folder">📁 Dossier</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreating(false)}>Annuler</Button>
                        <Button onClick={submitCreateSpace} data-testid="submit-create-space">Créer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Recherche globale de fichiers du Hub */}
            <HubFileSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />

            {/* Visite guidée du Hub — auto à la 1ère connexion, rejouable via le bouton */}
            <HubIntroTour forceOpen={tourOpen} onClose={() => setTourOpen(false)} />

            {/* Confirmation suppression d'espace — cascade si des projets existent */}
            <AlertDialog open={!!spaceToDelete} onOpenChange={(o) => !o && setSpaceToDelete(null)}>
                <AlertDialogContent data-testid="delete-space-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer l&apos;espace « {spaceToDelete?.name} » ?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-2 text-sm">
                                {spaceToDelete?.projectsCount > 0 ? (
                                    <>
                                        <p>Cet espace contient <strong>{spaceToDelete.projectsCount} projet{spaceToDelete.projectsCount > 1 ? 's' : ''}</strong>.</p>
                                        <p className="text-red-600 font-medium">
                                            La suppression forcée effacera aussi tous les projets, tâches, murs et historiques associés. Cette action est irréversible.
                                        </p>
                                    </>
                                ) : (
                                    <p>Cet espace est vide. Sa suppression est immédiate et irréversible.</p>
                                )}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2">
                        <AlertDialogCancel disabled={deletingSpace} data-testid="delete-space-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); handleDeleteSpace(spaceToDelete?.projectsCount > 0); }}
                            disabled={deletingSpace}
                            data-testid="delete-space-confirm"
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            {deletingSpace ? 'Suppression…' : (spaceToDelete?.projectsCount > 0 ? 'Tout supprimer' : 'Supprimer')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
        </DashboardLayout>
    );
}

function KpiCard({ icon: Icon, label, value, color, testId, hint, onClick }) {
    const content = (
        <CardContent className="p-4 flex items-center gap-3">
            <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center bg-slate-50", color)}>
                <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-2xl font-bold text-slate-900">{value}</div>
                <div className="text-xs text-slate-500 truncate flex items-center gap-1">
                    {label}
                    {hint && <InfoHint text={hint} />}
                </div>
            </div>
            {onClick && <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all shrink-0" />}
        </CardContent>
    );
    if (onClick) {
        return (
            <button type="button" onClick={onClick} data-testid={testId} className="text-left group">
                <Card className="cursor-pointer hover:shadow-md hover:border-indigo-300 transition-all h-full">
                    {content}
                </Card>
            </button>
        );
    }
    return (
        <Card data-testid={testId}>
            {content}
        </Card>
    );
}

// Wrapper repliable pour le mur d'idées général — s'ouvre automatiquement s'il y a des non-lus
function CollapsibleWall(props) {
    const KEY = 'valnexo_hub_general_wall_open';
    const [open, setOpen] = useState(() => {
        try { return localStorage.getItem(KEY) === '1'; } catch { return false; }
    });
    const [unread, setUnread] = useState(0);
    const seenTriggered = useRef(false);

    // Charger le nombre de non-lus
    useEffect(() => {
        let alive = true;
        const fetchUnread = async () => {
            try {
                const r = await hubAPI.wallUnreadCount();
                if (alive) setUnread(r.data?.count || 0);
            } catch { /* noop */ }
        };
        fetchUnread();
        const iv = setInterval(fetchUnread, 45000);
        return () => { alive = false; clearInterval(iv); };
    }, []);

    // Auto-ouvrir dès qu'il y a des non-lus (l'utilisateur ne peut pas passer à côté)
    useEffect(() => {
        if (unread > 0 && !open) {
            setOpen(true);
            try { localStorage.setItem(KEY, '1'); } catch { /* noop */ }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unread]);

    // Auto-marquer comme vu après 4s d'exposition à un mur ouvert avec des non-lus
    useEffect(() => {
        if (!open || unread === 0 || seenTriggered.current) return;
        const t = setTimeout(async () => {
            seenTriggered.current = true;
            try { await hubAPI.markWallSeen(); } catch { /* noop */ }
            setUnread(0);
            try { window.dispatchEvent(new Event('hub:wall-seen')); } catch { /* noop */ }
        }, 4000);
        return () => clearTimeout(t);
    }, [open, unread]);

    const toggle = () => {
        setOpen((v) => {
            const nv = !v;
            try { localStorage.setItem(KEY, nv ? '1' : '0'); } catch { /* noop */ }
            return nv;
        });
    };

    const attention = unread > 0 && !open;

    return (
        <div
            className={cn(
                'rounded-2xl border overflow-hidden transition-all',
                attention
                    ? 'border-amber-300 bg-gradient-to-r from-amber-50 to-white ring-2 ring-amber-200/60 shadow-sm animate-pulse-slow'
                    : 'border-slate-200 bg-white',
            )}
            data-testid="collapsible-wall"
        >
            <button
                onClick={toggle}
                className={cn(
                    'w-full flex items-center justify-between px-4 py-3 transition-colors',
                    attention ? 'hover:bg-amber-100/40' : 'hover:bg-slate-50',
                )}
                data-testid="wall-toggle"
            >
                <div className="flex items-center gap-2">
                    <Lightbulb className={cn('w-4 h-4', attention ? 'text-amber-500' : 'text-amber-500')} />
                    <span className="text-sm font-semibold text-slate-800">Mur d&apos;idées & propositions</span>
                    <span className="text-[11px] text-slate-400 hidden sm:inline">— Suggérez un projet, votez, commentez</span>
                    {unread > 0 && (
                        <Badge
                            className="bg-red-500 hover:bg-red-500 text-white text-[10px] px-1.5 py-0 animate-bounce-slow"
                            data-testid="wall-unread-badge"
                        >
                            {unread} nouveau{unread > 1 ? 'x' : ''}
                        </Badge>
                    )}
                </div>
                {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            {open && (
                <div className="border-t border-slate-100 p-4">
                    <GeneralWall {...props} />
                </div>
            )}
        </div>
    );
}

